import React, { useMemo } from 'react'
import { Card, Collapse, Divider } from 'antd'
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { currency } from 'utils/formatting'
import { ItemRow, ItemsRow } from 'components/apps/Reports/PosShift/Detail/ItemsRow'
import SectionHeader from 'components/apps/Reports/PosShift/Detail/SectionHeader'
import { PAYMENT_TYPE, paymentTypes } from 'utils/static'
import styles from './detail.module.scss'

const { Panel } = Collapse

const matchPaymentType = (paymentTypeId, type) =>
  paymentTypeId === paymentTypes[type] || paymentTypeId === PAYMENT_TYPE[type]

const groupPayments = (payments = []) => {
  if (!Array.isArray(payments)) {
    return []
  }

  const grouped = {}

  payments.forEach((payment, index) => {
    const cashlessPayment = payment?.cashlessPayment || {}

    const paymentId = cashlessPayment?.id ?? payment?.payment_type_id ?? `unknown-${index}`

    if (!grouped[paymentId]) {
      grouped[paymentId] = {
        id: paymentId,
        name: cashlessPayment?.name || payment?.payment_type_name || 'Unknown Payment',
        amount: 0,
        totalTransaction: 0,
        items: [],
      }
    }

    grouped[paymentId].amount += Number(payment?.amount_after_tax) || 0
    grouped[paymentId].totalTransaction += 1
    grouped[paymentId].items.push(payment)
  })

  return Object.values(grouped)
}

const expandIcon = ({ isActive }) => (
  <span className={`${styles.expandIcon} ${isActive ? styles.expandIconActive : ''}`}>
    {isActive ? <MinusOutlined /> : <PlusOutlined />}
  </span>
)

const PaymentDetailRow = ({ payment, index }) => (
  <div className={styles.paymentDetailRow}>
    <div className={styles.paymentDetailRefs}>
      <span className={styles.paymentDetailInvoiceRef}>
        {index}. {payment.invoice_ref_number || '-'}
      </span>
    </div>
    <span className={styles.paymentDetailAmount}>{currency(payment.amount_after_tax)}</span>
  </div>
)

const PaymentBreakdown = ({ payments = [] }) => {
  const { formatMessage } = useIntl()
  const groupedPayments = useMemo(() => groupPayments(payments), [payments])

  if (!groupedPayments.length) {
    return null
  }

  return (
    <Collapse ghost expandIcon={expandIcon} className={styles.paymentCollapse}>
      {groupedPayments.map((group) => (
        <Panel
          key={group.id}
          header={
            <div className={styles.categoryHeader}>
              <span className={styles.categoryName}>
                <CheckCircleOutlined className={styles.paymentGroupCheckmark} />
                {group.name}
                <span className={styles.categoryCount}>
                  ({group.totalTransaction}{' '}
                  {formatMessage({ id: 'report.transactions' }).toLowerCase()})
                </span>
              </span>
              <span className={styles.categoryTotal}>{currency(group.amount)}</span>
            </div>
          }
        >
          <div className={styles.paymentDetailList}>
            {group.items.map((payment, index) => (
              <PaymentDetailRow
                key={payment.payment_id ?? payment.invoice_id ?? `${group.id}-${index}`}
                payment={payment}
                index={index + 1}
              />
            ))}
          </div>
        </Panel>
      ))}
    </Collapse>
  )
}

const PaymentMethod = ({ data, loading }) => {
  const { formatMessage } = useIntl()

  const { edcPayments, transferPayments, depositPayments } = useMemo(() => {
    const payments = Array.isArray(data?.payments) ? data.payments : []

    return {
      edcPayments: payments.filter((p) => matchPaymentType(p.payment_type_id, 'EDC')),

      transferPayments: payments.filter((p) =>
        matchPaymentType(p.payment_type_id, 'TRANSFER_BANK'),
      ),

      depositPayments: payments.filter((p) => matchPaymentType(p.payment_type_id, 'DEPOSIT')),
    }
  }, [data?.payments])

  const cashPayment = Number(data?.cash_payment) || 0
  const cashlessPayment = Number(data?.cashless_payment) || 0
  const edcPayment = Number(data?.edc_payment) || 0
  const transferPayment = Number(data?.transfer_payment) || 0
  const depositPayment = Number(data?.deposit_payment) || 0

  const hasNonCash = edcPayment > 0 || transferPayment > 0 || depositPayment > 0

  const totalPayment = cashPayment + cashlessPayment

  return (
    <Card
      className={styles.card}
      loading={loading}
      title={
        <SectionHeader
          icon={<CreditCardOutlined />}
          iconClassName={styles.iconPurple}
          title={formatMessage({ id: 'report.payment_method' })}
        />
      }
    >
      <ItemsRow>
        <ItemRow
          title={formatMessage({ id: 'report.cash_payment' })}
          amount={currency(cashPayment)}
        />

        {hasNonCash ? (
          <>
            <div className={styles.subgroupTitle}>
              {formatMessage({ id: 'report.non_cash_payment' })}
            </div>

            <div className={styles.subgroupBody}>
              {edcPayment > 0 ? (
                <>
                  <ItemRow
                    title={formatMessage({ id: 'report.edc_payment' })}
                    amount={currency(edcPayment)}
                  />

                  <PaymentBreakdown payments={edcPayments} />
                </>
              ) : null}

              {transferPayment > 0 ? (
                <>
                  <ItemRow
                    title={formatMessage({ id: 'report.transfer_payment' })}
                    amount={currency(transferPayment)}
                  />

                  <PaymentBreakdown payments={transferPayments} />
                </>
              ) : null}

              {depositPayment > 0 ? (
                <>
                  <ItemRow
                    title={formatMessage({ id: 'report.deposit_payment' })}
                    amount={currency(depositPayment)}
                  />

                  <PaymentBreakdown payments={depositPayments} />
                </>
              ) : null}
            </div>

            <Divider style={{ margin: '4px 0' }} />

            <ItemRow
              title={formatMessage({ id: 'report.total_non_cash' })}
              amount={currency(cashlessPayment)}
              bold
            />
          </>
        ) : null}

        <Divider style={{ margin: '4px 0' }} />

        <ItemRow
          title={formatMessage({ id: 'report.total_payment' })}
          amount={currency(totalPayment)}
          bold
        />
      </ItemsRow>
    </Card>
  )
}

export default PaymentMethod
